// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The evidence tile's "Del op" view: the Segmentering flow (spec B3) on one
 * of the part's frames, inside the tile. Click = point prompt, drag = box
 * (`SegmentStage`); "Kør segmentering" runs SAM3 through the managed-model
 * runner (`useSam3().run`: waits out a download/build) with `save: true`, the
 * saved label PNG is read back for the overlay and the pixel counts, and
 * "Opret ressource fra markering" files the selected prompt's pixels as a new
 * instance and part (`POST /frames/{id}/segment/resource`, echoing the run's
 * `mask_revision`), then tells the viewers the label clouds changed.
 *
 * The frame opens seeded with a point at the part's projected centroid, so
 * one click on "Kør segmentering" usually cuts out the part itself.
 *
 * The rest of Segmentering sits one disclosure down ("Flere værktøjer",
 * `SegmentOptions`): text prompts and the label library, the confidence
 * slider, and "Tilføj til kø" for the neighbouring frames. With no prompt the
 * run uses the model's standard classes. "Åbn i Segmentering" carries the
 * frame and the seed to the full page (`/segmentering?frame=&u=&v=`).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { ApiRequestError, api } from '../../../api/client';
import type {
  FrameSegmentPrompt,
  FrameSegmentResult,
  SegmentResourceRequest,
  SegmentResourceResult,
  SurveyType,
  VisibleFrame,
} from '../../../api/types';
import { useCaseStore } from '../../../app/bygning/CaseDataContext';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { segmentHref } from '../../../app/links';
import { useJobs } from '../../../app/JobsContext';
import { useAsync } from '../../../app/useAsync';
import { useMutationQueue } from '../../../app/useMutationQueue';
import { useSam3 } from '../../../app/useSam3';
import { runLabel } from '../../../bygning/delop';
import { decodeLabelPng, promptPixelCounts, type LabelImage } from '../../../data/labelPng';
import { SegmentCancelled, SegmentRunError } from '../../../data/sam3Provisioning';
import {
  buildRequestPrompts,
  geometryHint,
  geometryNotice,
  offscreenRunError,
  pointBox,
  resourceBlockReason,
  resultClasses,
  seedToImage,
  type ImageBox,
  type ResultClass,
  type SegPrompt,
  type SentPrompt,
} from '../../../data/segmentView';
import { readLabelPalette } from '../../../viewport/labelColors';
import { Button } from '../../Button';
import { Sam3StatusChip } from '../../Sam3StatusChip';
import { Select } from '../../Select';
import { TextLink } from '../../TextLink';
import { ResourceDialog } from '../../segmentering/ResourceDialog';
import { SegmentStage } from '../../segmentering/SegmentStage';
import { SegmentOptions } from './SegmentOptions';
import styles from './SegmentView.module.css';

let nextPromptId = 0;
const newPromptId = () => `ev${++nextPromptId}`;

/** The run's starting confidence: Segmentering's default. */
const CONFIDENCE = 0.5;
/** Frames offered in the frame picker. */
const FRAME_CHOICES = 40;

export interface SegmentTarget {
  frameId: number;
  /** The part's projected centroid in that frame (sensor pixels), seeding a point prompt. */
  seed: { u: number; v: number } | null;
}

export interface SegmentViewProps {
  target: SegmentTarget;
  /** The part's frames, for the picker (most central first). */
  frames: readonly VisibleFrame[];
  onTarget: (target: SegmentTarget) => void;
  /** Live survey types for the resource dialog. */
  types: readonly SurveyType[];
  onCreated: (result: SegmentResourceResult) => void;
}

interface RunOutcome {
  frameId: number;
  result: FrameSegmentResult;
  sent: SentPrompt[];
  wire: FrameSegmentPrompt[];
}

async function fetchLabelImage(frameId: number): Promise<LabelImage> {
  const response = await fetch(`${api.frameImageUrl(frameId, 'segmentation')}&_t=${Date.now()}`);
  if (!response.ok) throw new Error(`segmenteringen kunne ikke hentes (${response.status})`);
  return decodeLabelPng(new Uint8Array(await response.arrayBuffer()));
}

function errorText(e: unknown): string {
  if (e instanceof ApiRequestError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

export function SegmentView({ target, frames, onTarget, types, onCreated }: SegmentViewProps) {
  const canEdit = useCanEdit();
  const frameId = target.frameId;
  const frame = useAsync((signal) => api.frame(frameId, signal), [frameId]);
  const sam3 = useSam3();
  const { markCloudsChanged } = useJobs();
  const { actions } = useCaseStore();
  const paletteSize = useMemo(() => Math.max(1, readLabelPalette().colors.length), []);

  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [prompts, setPrompts] = useState<SegPrompt[]>([]);
  const [confidence, setConfidence] = useState(CONFIDENCE);
  // The frame a run is in flight for (null = idle): the frame may change under it.
  const [runningFrame, setRunningFrame] = useState<number | null>(null);
  const running = runningFrame !== null;
  // Touch only: a finger drag draws a box instead of panning (28f5006).
  const [touchDraw, setTouchDraw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);
  const [mask, setMask] = useState<LabelImage | null>(null);
  const [maskError, setMaskError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [dialogFor, setDialogFor] = useState<ResultClass | null>(null);
  const [dialogTarget, setDialogTarget] = useState<{ frameId: number; maskRevision: string | null } | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [labelNames, setLabelNames] = useState<Record<string, string> | null>(null);

  const currentFrame = useRef(frameId);
  currentFrame.current = frameId;

  const runQueue = useMutationQueue({ scope: 'page', onError: (e) => setError(errorText(e)) });
  const writeQueue = useMutationQueue({ onError: (e) => setDialogError(errorText(e)) });

  // A new frame starts clean.
  useEffect(() => {
    setSize(null);
    setPrompts([]);
    setOutcome(null);
    setMask(null);
    setMaskError(null);
    setSelected(null);
    setError(null);
  }, [frameId]);

  // Seed a point at the part's centroid once the image size is known.
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    const seed = target.seed;
    if (!seed || !size || !frame.data || frame.data.id !== frameId) return;
    const key = `${frameId}:${seed.u}:${seed.v}`;
    if (seededKey.current === key) return;
    seededKey.current = key;
    const at = seedToImage(seed, frame.data.intrinsics, size);
    if (!at) return;
    setPrompts((cur) => [
      ...cur,
      { id: newPromptId(), text: '', box: pointBox(at.x, at.y, size.width, size.height), point: true, at: [at.x, at.y] },
    ]);
  }, [frameId, target.seed, size, frame.data]);

  const slots = useMemo(() => {
    const map = new Map<string, number>();
    buildRequestPrompts(prompts).sent.forEach((s, i) => map.set(s.promptId, i));
    return map;
  }, [prompts]);
  const slotOf = useCallback((id: string) => slots.get(id) ?? null, [slots]);

  const counts = useMemo(() => (mask ? promptPixelCounts(mask) : null), [mask]);
  const resultIsCurrent = outcome !== null && outcome.frameId === frameId;
  const classes = useMemo(
    () => (outcome && resultIsCurrent ? resultClasses(outcome.result, outcome.sent, counts) : []),
    [outcome, resultIsCurrent, counts],
  );

  const addDrawn = useCallback((box: ImageBox, at: [number, number] | null) => {
    setPrompts((cur) => [
      ...cur,
      at ? { id: newPromptId(), text: '', box, point: true, at } : { id: newPromptId(), text: '', box, point: false },
    ]);
    // A new prompt makes the old mask a different question.
    setOutcome(null);
    setMask(null);
    setSelected(null);
  }, []);

  const promptCount = slots.size;

  const run = useCallback(() => {
    if (running || !canEdit) return;
    const { prompts: wire, sent } = buildRequestPrompts(prompts);
    const id = frameId;
    const stillHere = () => currentFrame.current === id;
    setRunningFrame(id);
    setError(null);
    void runQueue.mutate(async () => {
      try {
        const result = await sam3.run(() => api.segmentFrame(id, { prompts: wire.length > 0 ? wire : undefined, confidence, save: true }));
        if (!stillHere()) return;
        setOutcome({ frameId: id, result, sent, wire });
        const reported = new Set([...sent.map((_, i) => i), ...Object.keys(result.labels).map(Number)]);
        setSelected(reported.size === 1 ? [...reported][0] : null);
        setMask(null);
        setMaskError(null);
        if (result.saved) {
          try {
            const image = await fetchLabelImage(id);
            if (stillHere()) setMask(image);
          } catch (e) {
            if (stillHere()) setMaskError(errorText(e));
          }
        }
      } catch (e) {
        if (e instanceof SegmentCancelled) return;
        const message = e instanceof SegmentRunError ? e.message : errorText(e);
        // A failure after the user moved on must still be heard, naming the
        // frame it was for (119191a) — not dropped silently.
        if (stillHere()) setError(message);
        else actions.notify(offscreenRunError(id, message));
      } finally {
        setRunningFrame(null);
      }
    });
  }, [running, canEdit, prompts, confidence, frameId, runQueue, sam3, actions]);

  const reset = () => {
    setPrompts([]);
    setOutcome(null);
    setMask(null);
    setMaskError(null);
    setSelected(null);
    setError(null);
  };

  const openDialog = () => {
    const cls = classes.find((c) => c.index === selected);
    if (!cls || !outcome) return;
    setDialogTarget({ frameId: outcome.frameId, maskRevision: outcome.result.mask_revision ?? null });
    setDialogFor(cls);
    setDialogError(null);
    api.clouds().then(
      (clouds) => setLabelNames(clouds.find((c) => c.name === 'labels')?.labels ?? null),
      () => setLabelNames(null),
    );
  };

  const createResource = (request: SegmentResourceRequest) => {
    if (!dialogTarget) return;
    const id = dialogTarget.frameId;
    void writeQueue.mutate(
      async () => {
        const result = await api.segmentResource(id, request);
        setDialogFor(null);
        markCloudsChanged(result.clouds);
        onCreated(result);
      },
      (cause) => setDialogError(cause.message),
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !dialogFor) {
      e.preventDefault();
      run();
    }
  };

  const blockReason = resourceBlockReason(frame.data && frame.data.id === frameId ? frame.data : undefined);
  const chosen = classes.find((c) => c.index === selected) ?? null;
  const maskPixels = chosen?.pixels ?? (resultIsCurrent ? outcome.result.labeled_pixels : null);
  const notice = resultIsCurrent ? geometryNotice(outcome.result, outcome.wire) : null;
  const hint = notice ?? (resultIsCurrent ? geometryHint(classes, prompts) : null);
  const canCreate =
    canEdit && resultIsCurrent && outcome.result.saved && chosen !== null && chosen.pixels !== 0 && blockReason === null && !writeQueue.busy;
  const createWhy = !resultIsCurrent
    ? null
    : !outcome.result.saved
      ? 'Masken blev ikke gemt.'
      : (blockReason ?? (chosen === null ? 'Vælg en markering ovenfor.' : chosen.pixels === 0 ? 'Markeringen har ingen pixels.' : null));
  const sam3Ready = sam3.view.phase === 'ready';

  const choices = frames.slice(0, FRAME_CHOICES);
  const inChoices = choices.some((f) => f.frame_id === frameId);

  return (
    <div className={styles.view} onKeyDown={onKeyDown}>
      <div className={styles.toolbar}>
        <label className={styles.framePick}>
          <span className={styles.muted}>Frame</span>
          <Select
            variant="outline"
            size="sm"
            value={String(frameId)}
            disabled={running}
            onChange={(e) => {
              const f = frames.find((x) => x.frame_id === Number(e.target.value));
              if (f) onTarget({ frameId: f.frame_id, seed: { u: f.u, v: f.v } });
            }}
          >
            {!inChoices && <option value={String(frameId)}>{frameId}</option>}
            {choices.map((f, i) => (
              <option key={f.frame_id} value={String(f.frame_id)}>
                {f.frame_id}
                {i === 0 ? ' · mest central' : ''}
              </option>
            ))}
          </Select>
        </label>
        {sam3.available && <Sam3StatusChip view={sam3.view} compact={sam3Ready || sam3.view.phase === 'unknown'} />}
        <TextLink
          size="sm"
          className={styles.end}
          to={segmentHref(frameId, target.seed)}
          title="Hele Segmentering-værktøjet: filmstrimmel, maske til/fra, tastatur"
        >
          Åbn i Segmentering
        </TextLink>
      </div>

      <div className={styles.stage}>
        <SegmentStage
          key={frameId}
          imageUrl={api.frameImageUrl(frameId, 'color')}
          alt={`Frame ${frameId} — klik et punkt eller træk en boks om det, der skal deles op`}
          size={size}
          onLoad={setSize}
          prompts={prompts}
          slotOf={slotOf}
          paletteSize={paletteSize}
          onDraw={addDrawn}
          mask={resultIsCurrent ? mask : null}
          showMask
          selected={selected}
          disabled={running || !canEdit}
          touchDraw={touchDraw}
        />
        <span className={styles.label}>Frame {frameId} · klik = punkt · træk = boks</span>
      </div>

      {classes.length > 1 && (
        <div className={styles.classes} role="group" aria-label="Markeringer">
          {classes.map((c) => (
            <button
              key={c.index}
              type="button"
              className={styles.cls}
              aria-pressed={selected === c.index}
              onClick={() => setSelected((s) => (s === c.index ? null : c.index))}
            >
              <span className={styles.swatch} style={{ '--c': `var(--label-${c.index % paletteSize})` } as CSSProperties}>
                {c.index + 1}
              </span>
              {c.name}
              <span className={styles.muted}>{c.pixels === null ? '…' : `${c.pixels.toLocaleString('da-DK')} px`}</span>
            </button>
          ))}
        </div>
      )}

      {(error || maskError || hint) && (
        <p className={error ? styles.error : styles.note} role={error ? 'alert' : 'status'}>
          {error ?? (maskError ? `Masken kunne ikke vises: ${maskError}` : hint)}
        </p>
      )}

      <SegmentOptions
        frameId={frameId}
        prompts={prompts}
        onPrompts={(next) => {
          // Removing or adding a prompt changes the question; renaming one does not.
          if (next.length !== prompts.length) {
            setOutcome(null);
            setMask(null);
            setSelected(null);
          }
          setPrompts(next);
        }}
        confidence={confidence}
        onConfidence={setConfidence}
        slotOf={slotOf}
        paletteSize={paletteSize}
        newId={newPromptId}
        disabled={running || !canEdit}
        onQueued={(n) => actions.notify(`${n} ${n === 1 ? 'billede' : 'billeder'} lagt i køen · kør den fra Billeder`)}
      />

      <div className={styles.foot}>
        {canEdit && (
          <Button
            onClick={run}
            disabled={running}
            aria-busy={running}
            title={promptCount === 0 ? 'Uden prompts finder modellen sine standardklasser · Ctrl/⌘ + Enter' : 'Ctrl/⌘ + Enter'}
          >
            {runLabel({ running, phase: sam3.view.phase, promptCount, runningFrame, frameId })}
          </Button>
        )}
        <Button onClick={reset} disabled={running || (prompts.length === 0 && !outcome)}>
          Nulstil
        </Button>
        {canEdit && (
          <Button
            className={styles.touchOnly}
            aria-pressed={touchDraw}
            onClick={() => setTouchDraw((v) => !v)}
            title="Med en finger: træk en boks på billedet i stedet for at rulle siden"
          >
            {touchDraw ? 'Tegn boks: til' : 'Tegn boks'}
          </Button>
        )}
        <span className={styles.muted} role="status">
          {promptCount === 1 ? '1 prompt' : `${promptCount} prompts`}
          {resultIsCurrent && maskPixels !== null ? ` · maske ${maskPixels.toLocaleString('da-DK')} px` : ''}
          {resultIsCurrent && !outcome.result.saved ? ' (ikke gemt)' : ''}
          {sam3.available && sam3Ready ? ' · SAM3 klar' : ''}
        </span>
        {canEdit && (
          <Button
            variant="primary"
            className={styles.end}
            disabled={!canCreate}
            title={createWhy ?? undefined}
            aria-haspopup="dialog"
            onClick={openDialog}
          >
            Opret ressource fra markering ▾
          </Button>
        )}
        {!canEdit && <span className={`${styles.muted} ${styles.end}`}>Kun redaktører kan dele op.</span>}
      </div>

      {dialogFor &&
        createPortal(
          <ResourceDialog
            cls={dialogFor}
            types={[...types]}
            labelNames={labelNames}
            maskRevision={dialogTarget?.maskRevision ?? null}
            busy={writeQueue.busy}
            error={dialogError}
            onCancel={() => setDialogFor(null)}
            onSubmit={createResource}
          />,
          document.body,
        )}
    </div>
  );
}
